import React from "react";
import ExpandableFieldValue from "../../../components/ExpandableFieldValue";
import { getBadgeClassName } from "../../../utils/recordMappers";
import DrugSubmissionsFieldValue from "./DrugSubmissionsFieldValue";
import RelatedRecordsField from "./RelatedRecordsField";

const badgeBaseClass =
  "inline-flex max-w-full items-center rounded-md px-2.5 py-1 text-[11px] font-semibold tracking-[0.02em]";

const isBadgeKind = (kind) =>
  kind === "status" || kind === "accent" || kind === "id" || kind === "neutral-badge";

const RecordFieldValue = ({ field, theme, onOpenLinkedRecord }) => {
  if (field.kind === "drug-submissions") {
    return <DrugSubmissionsFieldValue value={field.displayValue} />;
  }

  if (field.kind === "multi-badge") {
    const items = Array.isArray(field.displayValue)
      ? field.displayValue
      : String(field.value || "")
          .split("\n")
          .map((item) => item.trim())
          .filter(Boolean);

    return (
        <div className="flex flex-wrap gap-2 md:max-w-[70%] md:justify-end">
        {items.map((item) => (
          <span
            key={`${field.label}-${item}`}
            className={`${badgeBaseClass} ${getBadgeClassName(field.itemKind || "neutral-badge", item, theme)}`}
          >
            {item}
          </span>
        ))}
      </div>
    );
  }

  if (field.kind === "link" && field.href) {
    return (
      <div className="md:max-w-[70%]">
        <a
          href={field.href}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 break-all text-sm font-medium leading-6 text-sky-300 transition-colors hover:text-sky-200 md:justify-end md:text-right"
        >
          <span>{field.value}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            className="h-4 w-4 shrink-0"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 17 17 7M9 7h8v8" />
          </svg>
        </a>
      </div>
    );
  }

  if (field.kind === "multi-link" && field.links?.length) {
    return (
      <div className="flex flex-wrap gap-2 md:max-w-[70%] md:justify-end">
        {field.links.map((link) => (
          <a
            key={`${field.label}-${link.href}`}
            href={link.href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 break-all rounded-full border border-sky-500/40 px-2.5 py-1 text-xs font-medium leading-5 text-sky-300 transition-colors hover:border-sky-400 hover:text-sky-200"
          >
            <span>{link.text}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              className="h-3.5 w-3.5 shrink-0"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 17 17 7M9 7h8v8" />
            </svg>
          </a>
        ))}
      </div>
    );
  }

  if (field.kind === "related-records") {
    return <RelatedRecordsField field={field} onOpenLinkedRecord={onOpenLinkedRecord} />;
  }

  if (isBadgeKind(field.kind)) {
    return (
      <div className="md:max-w-[70%]">
        <span className={`${badgeBaseClass} ${getBadgeClassName(field.kind, field.value, theme)}`}>
          {field.value}
        </span>
      </div>
    );
  }

  return <ExpandableFieldValue value={field.value} />;
};

export default RecordFieldValue;
